import React, { useState } from 'react';
import { Row, Col, Form, FormGroup, Label, Input, Button, Alert, Modal, ModalHeader, ModalBody, ModalFooter, Collapse } from 'reactstrap';
import InputsValidations, { validateContactEmail, validateContactPhone, filterContactPhone, getMaxBirthDate } from '../../../../modules/InputsValidations.js';
import AddOwnerAddressModal from '../components/AddOwnerAddressModal';
import ConfirmationModal from 'components/ConfirmationModal';

function OwnerInfoStep({ value, onChange, errors = {}, owners = [], onOwnersChange, locked = false, isUpdateModal = false, mode = 'APPLICATION' }) {
	const getDefaultOwner = () => ({
		firstName: '',
		middleName: '',
		lastName: '',
		gender: 'MALE',
		birthDate: '',
		citizenship: '',
		ownershipPercentage: '',
		socialSecurityNumber: '',
		contacts: [
			{
				id: Date.now() + Math.floor(Math.random() * 9999999),
				type: 'EMAIL',
				value: '',
				isPrimary: true,
			},
			{
				id: Date.now() + Math.floor(Math.random() * 9999999),
				type: 'PHONE',
				value: '',
				isPrimary: true,
			}
		],
		addresses: []
	});
	
	const [currentOwner, setCurrentOwner] = useState(getDefaultOwner);

	const [ownerErrors, setOwnerErrors] = useState({});
	const [contactErrors, setContactErrors] = useState({});
	const [showAddressModal, setShowAddressModal] = useState(false);
	const [addressToEdit, setAddressToEdit] = useState(null);
	const [showDeleteOwnerModal, setShowDeleteOwnerModal] = useState(false);
	const [showDiscardOwnerModal, setShowDiscardOwnerModal] = useState(false);
	const [showCancelOwnerEditModal, setShowCancelOwnerEditModal] = useState(false);
	const [ownerIndexToEdit, setOwnerIndexToEdit] = useState(null);
	const [showAddOwnerForm, setShowAddOwnerForm] = useState(owners.length === 0);

	// Delete confirmation modals
	const [showDeleteAddressModal, setShowDeleteAddressModal] = useState(false);
	const [addressToDelete, setAddressToDelete] = useState(null);
	const [showDeleteContactModal, setShowDeleteContactModal] = useState(false);
	const [contactToDelete, setContactToDelete] = useState(null);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [ownerToEdit, setOwnerToEdit] = useState(null);
	const [showEditConfirmationModal, setShowEditConfirmationModal] = useState(false);
	const [lastAction, setLastAction] = useState('added'); // 'added' or 'updated'
	
	// Edit owner modal
	const [ownerIndexToEditModal, setOwnerIndexToEditModal] = useState(null);

	// Inline feedback modal for validation errors on Add Owner
	const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
	const [feedbackMessage, setFeedbackMessage] = useState('');
	const openFeedback = (message) => { setFeedbackMessage(message); setFeedbackModalOpen(true); };
	const closeFeedback = () => setFeedbackModalOpen(false);

	// Available address types for current owner - permitir múltiples del mismo tipo
	const allAddressTypes = ['PHYSICAL', 'MAILING', 'BILLING'];
	const availableAddressTypes = allAddressTypes;

	// Check if form is being filled (for UpdateMerchantModal button disabling)
	const isFormBeingFilled = () => {
		if (!isUpdateModal) return true; // Always enable buttons in normal wizard mode
		
		const hasBasicInfo = currentOwner.firstName.trim() !== '' || 
			currentOwner.lastName.trim() !== '' ||
			currentOwner.socialSecurityNumber.trim() !== '' ||
			currentOwner.ownershipPercentage.toString().trim() !== '' ||
			currentOwner.citizenship.trim() !== '' ||
			currentOwner.birthDate.trim() !== '';
			
		return hasBasicInfo;
	};

	const resetCurrentOwner = () => {
		setCurrentOwner(getDefaultOwner());
	};

	// Check if form has any data that would be lost
	const hasFormData = () => {
		return currentOwner.firstName.trim() !== '' || 
			currentOwner.lastName.trim() !== '' ||
			currentOwner.middleName.trim() !== '' ||
			currentOwner.socialSecurityNumber.trim() !== '' ||
			currentOwner.ownershipPercentage.toString().trim() !== '' ||
			currentOwner.citizenship.trim() !== '' ||
			currentOwner.birthDate.trim() !== '' ||
			(currentOwner.contacts && currentOwner.contacts.filter(contact => contact.value !== '')).length > 0 ||
			(currentOwner.addresses && currentOwner.addresses.length > 0);
	};

	const handle = (e) => {
		const { name, value } = e.target;
		let processedValue = value;
		
		// Apply filtering for specific fields
		if (name === 'firstName' || name === 'middleName' || name === 'lastName') {
			processedValue = InputsValidations.filterOwnerName(value);
		} else if (name === 'citizenship') {
			processedValue = InputsValidations.filterCitizenship(value);
		} else if (name === 'ownershipPercentage') {
			processedValue = InputsValidations.filterOwnershipPercentage(value);
		} else if (name === 'socialSecurityNumber') {
			// Format SSN automatically
			const digits = value.replace(/\D/g, '');
			if (digits.length <= 3) {
				processedValue = digits;
			} else if (digits.length <= 5) {
				processedValue = `${digits.slice(0, 3)}-${digits.slice(3)}`;
			} else {
				processedValue = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
			}
		}
		
		setCurrentOwner(prev => ({
			...prev,
			[name]: processedValue
		}));

		// Real-time validation for name fields
		if (name === 'firstName' || name === 'middleName' || name === 'lastName') {
			const isRequired = name === 'firstName' || name === 'lastName';
			const fieldName = name === 'firstName' ? 'First name' : name === 'middleName' ? 'Middle name' : 'Last name';
			
			const validationResult = InputsValidations.validateOwnerName(processedValue, fieldName, isRequired);
			
			if (validationResult.isValid) {
				setOwnerErrors(prev => {
					const newErrors = { ...prev };
					delete newErrors[name];
					return newErrors;
				});
			} else {
				setOwnerErrors(prev => ({
					...prev,
					[name]: validationResult.message
				}));
			}
		}
		
		// Real-time validation for birth date
		if (name === 'birthDate') {
			const validationResult = InputsValidations.validateBirthDate(processedValue);
			
			if (validationResult.isValid) {
				setOwnerErrors(prev => {
					const newErrors = { ...prev };
					delete newErrors[name];
					return newErrors;
				});
			} else {
				setOwnerErrors(prev => ({
					...prev,
					[name]: validationResult.message
				}));
			}
		}
		
		// Real-time validation for citizenship
		if (name === 'citizenship') {
			const validationResult = InputsValidations.validateCitizenship(processedValue);
			
			if (validationResult.isValid) {
				setOwnerErrors(prev => {
					const newErrors = { ...prev };
					delete newErrors[name];
					return newErrors;
				});
			} else {
				setOwnerErrors(prev => ({
					...prev,
					[name]: validationResult.message
				}));
			}
		}
		
		// Real-time validation for ownership percentage
		if (name === 'ownershipPercentage') {
			const validationResult = InputsValidations.validateOwnershipPercentage(processedValue);
			
			if (validationResult.isValid) {
				setOwnerErrors(prev => {
					const newErrors = { ...prev };
					delete newErrors[name];
					return newErrors;
				});
			} else {
				setOwnerErrors(prev => ({
					...prev,
					[name]: validationResult.message
				}));
			}
		}
		
		// Real-time validation for SSN
		if (name === 'socialSecurityNumber') {
			const validationResult = InputsValidations.validateSSN(processedValue);
			
			if (validationResult.isValid) {
				setOwnerErrors(prev => {
					const newErrors = { ...prev };
					delete newErrors[name];
					return newErrors;
				});
			} else {
				setOwnerErrors(prev => ({
					...prev,
					[name]: validationResult.message
				}));
			}
		}
	};

	// Address modal handlers
	const toggleAddressModal = () => {
		setShowAddressModal(!showAddressModal);
		if (!showAddressModal) {
			setAddressToEdit(null);
		}
	};

	const handleAddressAdded = (address, editingAddress) => {
		// No restriction on address types - multiple addresses of the same type are allowed
		if (editingAddress) {
			// Update existing address
			const updatedAddresses = currentOwner.addresses.map(a => 
				a === editingAddress ? address : a
			);
			setCurrentOwner(prev => ({ ...prev, addresses: updatedAddresses }));
		} else {
			// Add new address
			setCurrentOwner(prev => ({ 
				...prev, 
				addresses: [...(prev.addresses || []), address] 
			}));
		}
	};

	const handleEditAddress = (address) => {
		setAddressToEdit(address);
		setShowAddressModal(true);
	};

	const handleRemoveAddress = (addressToRemove) => {
		setAddressToDelete(addressToRemove);
		setShowDeleteAddressModal(true);
	};

	// Add owner to list
	const handleAddOwner = () => {
		const newErrors = {};
		
		if (!currentOwner.firstName || currentOwner.firstName.trim() === '') {
			newErrors.firstName = 'First name is required';
		}
		if (!currentOwner.lastName || currentOwner.lastName.trim() === '') {
			newErrors.lastName = 'Last name is required';
		}
		if (!currentOwner.socialSecurityNumber || currentOwner.socialSecurityNumber.trim() === '') {
			newErrors.socialSecurityNumber = 'SSN is required';
		}
		if (!currentOwner.ownershipPercentage || String(currentOwner.ownershipPercentage).trim() === '') {
			newErrors.ownershipPercentage = 'Ownership percentage is required';
		}
		if (!currentOwner.citizenship || currentOwner.citizenship.trim() === '') {
			newErrors.citizenship = 'Citizenship is required';
		}
		if (!currentOwner.birthDate || String(currentOwner.birthDate).trim() === '') {
			newErrors.birthDate = 'Date of birth is required';
		} else {
			// Validate age (must be at least 18 years old)
			const birthDateValidation = InputsValidations.validateBirthDate(currentOwner.birthDate, 'Birth Date');
			if (!birthDateValidation.isValid) {
				newErrors.birthDate = birthDateValidation.message;
			}
		}
		if (!currentOwner.addresses || currentOwner.addresses.length === 0) {
			newErrors.addresses = 'You must add one at least one address.';
		}

		// Validate contacts: require 1 primary phone and 1 primary email, and all values must be non-empty
		if (!currentOwner.contacts || currentOwner.contacts.length === 0) {
			newErrors.contacts = 'At least one primary phone and one primary email are required.';
		} else {
			// Check for empty contact values
			const emptyContacts = currentOwner.contacts.filter(c => !c.value || c.value.trim() === '');
			if (emptyContacts.length > 0) {
				const emptyEmailCount = emptyContacts.filter(c => c.type.toUpperCase() === 'EMAIL').length;
				const emptyPhoneCount = emptyContacts.filter(c => c.type.toUpperCase() === 'PHONE').length;
				
				let errorMessage = 'All contact values must be filled in.';
				if (emptyEmailCount > 0 && emptyPhoneCount > 0) {
					errorMessage = `All email and phone contact values must be filled in.`;
				} else if (emptyEmailCount > 0) {
					errorMessage = `All email contact values must be filled in.`;
				} else if (emptyPhoneCount > 0) {
					errorMessage = `All phone contact values must be filled in.`;
				}
				newErrors.contacts = errorMessage;
				
				// Mark individual empty contacts as invalid
				const newContactErrors = {};
				emptyContacts.forEach(contact => {
					if (contact.type.toUpperCase() === 'EMAIL') {
						newContactErrors[contact.id] = 'Email address is required';
					} else if (contact.type.toUpperCase() === 'PHONE') {
						newContactErrors[contact.id] = 'Phone number is required';
					}
				});
				setContactErrors(prev => ({ ...prev, ...newContactErrors }));
			} else {
				// Check for invalid email formats
				const emailContacts = currentOwner.contacts.filter(c => c.type === 'EMAIL');
				const invalidEmailContacts = emailContacts.filter(c => {
					const validation = validateContactEmail(c.value);
					return !validation.isValid;
				});
				
				if (invalidEmailContacts.length > 0) {
					newErrors.contacts = 'All email contact values must be filled in.';
					
					// Mark individual invalid email contacts as invalid
					const newContactErrors = {};
					invalidEmailContacts.forEach(contact => {
						const validation = validateContactEmail(contact.value);
						newContactErrors[contact.id] = validation.message;
					});
					setContactErrors(prev => ({ ...prev, ...newContactErrors }));
				} else {
					// Check for primary contacts
					const primaryEmail = currentOwner.contacts.filter(c => c.type.toUpperCase() === 'EMAIL' && c.isPrimary);

					if (primaryEmail.length === 0) {
						newErrors.contacts = 'A primary email contact is required.';
					} else if (primaryEmail.length > 1) {
						newErrors.contacts = 'Only 1 primary email contact is required.';
					}

					const primaryPhone = currentOwner.contacts.filter(c => c.type.toUpperCase() === 'PHONE' && c.isPrimary);

					if (primaryPhone.length === 0) {
						newErrors.contacts = 'A primary phone contact is required.';
					} else if (primaryPhone.length > 1) {
						newErrors.contacts = 'Only 1 primary phone contact is required.';
					}
				}
			}
		}

		if (Object.keys(newErrors).length > 0) {
			setOwnerErrors(newErrors);

			if (newErrors.contacts != null && newErrors.contacts !== '') {
				openFeedback(newErrors.contacts);
			} else {
				openFeedback(`You must complete all required fields before adding this owner.`);
			}

			return;
		}

		// Validate total ownership percentage does not exceed 100%
		const currentOwnershipValue = parseFloat(currentOwner.ownershipPercentage) || 0;
		
		let existingOwnersTotal;
		if (isEditMode && ownerIndexToEditModal !== null) {
			// When editing, exclude the owner being edited from the total
			existingOwnersTotal = (owners || []).reduce((sum, owner, index) => {
				return index === ownerIndexToEditModal ? sum : sum + (parseFloat(owner.ownershipPercentage) || 0);
			}, 0);
		} else {
			// When adding new owner, include all existing owners
			existingOwnersTotal = (owners || []).reduce((sum, owner) => {
				return sum + (parseFloat(owner.ownershipPercentage) || 0);
			}, 0);
		}
		
		const totalOwnership = existingOwnersTotal + currentOwnershipValue;

		if (totalOwnership > 100) {
			const action = isEditMode ? 'updating' : 'adding';
			newErrors.ownershipPercentage = `Total ownership cannot exceed 100%. Current total: ${existingOwnersTotal.toFixed(2)}% + ${currentOwnershipValue.toFixed(2)}% = ${totalOwnership.toFixed(2)}%`;
			setOwnerErrors(newErrors);
			openFeedback(`Total ownership percentage cannot exceed 100%. ${action.charAt(0).toUpperCase() + action.slice(1)} this owner would result in ${totalOwnership.toFixed(2)}% total ownership.`);
			return;
		}
			
		// Add owner to list (prepend to show newest first)
		const newOwner = {
			...currentOwner,
			contacts: currentOwner.contacts.filter(contact => 
				contact.value && contact.value.trim() !== ''
			),
			addresses: currentOwner.addresses.filter(address => 
				address.streetAddress && address.streetAddress.trim() !== '' && 
				address.city && address.city.trim() !== ''
			)
		};

		if (isEditMode && ownerIndexToEditModal !== null) {
			// Update existing owner
			const updatedOwners = owners.map((owner, i) => 
				i === ownerIndexToEditModal ? newOwner : owner
			);
			onOwnersChange(updatedOwners);
			setLastAction('updated');
			
			// Exit edit mode
			setIsEditMode(false);
			setOwnerToEdit(null);
			setOwnerIndexToEditModal(null);
		} else {
			// Add new owner
			onOwnersChange([newOwner, ...(owners || [])]);
			setLastAction('added');
		}
		
		resetCurrentOwner();
		setOwnerErrors({});
		
		// Show success modal
		setShowSuccessModal(true);
	};

	// Delete owner handlers
	const handleDeleteOwner = (index) => {
		setOwnerIndexToEdit(index);
		setShowDeleteOwnerModal(true);
	};

	const confirmDeleteOwner = () => {
		if (ownerIndexToEdit === null) return;
		const updatedOwners = owners.filter((_, i) => i !== ownerIndexToEdit);
		onOwnersChange(updatedOwners);
		setOwnerIndexToEdit(null);
		setShowDeleteOwnerModal(false);

		if (owners.length === 0) {
			setShowAddOwnerForm(true);
		}
	};

	const cancelDeleteOwner = () => {
		setOwnerIndexToEdit(null);
		setShowDeleteOwnerModal(false);
	};

	const confirmDiscardOwner = () => {
		resetCurrentOwner();
		setShowDiscardOwnerModal(false);

		if (isEditMode) {
			setShowCancelOwnerEditModal(false);
			cancelEditMode();
		}

		setShowAddOwnerForm(false);
		setOwnerErrors({});
	};

	const cancelDiscardOwner = () => {
		setShowDiscardOwnerModal(false);
	};

	const cancelDiscardOwnerEdit = () => {
		setShowCancelOwnerEditModal(false);
	};

	// Delete address confirmation handlers
	const confirmDeleteAddress = () => {
		if (addressToDelete) {
			const next = (currentOwner.addresses || []).filter(a => a !== addressToDelete);
			setCurrentOwner(prev => ({ ...prev, addresses: next }));
		}
		setAddressToDelete(null);
		setShowDeleteAddressModal(false);
	};

	const cancelDeleteAddress = () => {
		setAddressToDelete(null);
		setShowDeleteAddressModal(false);
	};

	// Delete contact confirmation handlers
	const confirmDeleteContact = () => {
		if (contactToDelete) {
			const next = (currentOwner.contacts || []).filter(c => c !== contactToDelete);
			setCurrentOwner(prev => ({ ...prev, contacts: next }));
		}
		setContactToDelete(null);
		setShowDeleteContactModal(false);
	};

	const cancelDeleteContact = () => {
		setContactToDelete(null);
		setShowDeleteContactModal(false);
	};

	// Edit owner handlers
	const handleEditOwner = (index) => {
		// Check if form has existing data
		if (hasFormData()) {
			// Store the owner to edit and show confirmation
			setOwnerToEdit(owners[index]);
			setShowEditConfirmationModal(true);
		} else {
			// No existing data, proceed directly to edit mode
			startEditMode(owners[index], index);
		}
	};

	// Start edit mode with owner data
	const startEditMode = (ownerData, index) => {
		setShowAddOwnerForm(true);
		setCurrentOwner(ownerData);
		setIsEditMode(true);
		setOwnerToEdit(ownerData);
		setOwnerIndexToEditModal(index);
		// Clear any existing errors
		setOwnerErrors({});
		setContactErrors({});
	};

	// Confirm edit and start edit mode
	const confirmEditOwner = () => {
		if (ownerToEdit) {
			startEditMode(ownerToEdit, owners.findIndex(owner => owner === ownerToEdit));
		}
		setShowEditConfirmationModal(false);
		setOwnerToEdit(null);
	};

	// Cancel edit mode
	const cancelEditMode = () => {
		setIsEditMode(false);
		setOwnerToEdit(null);
		setOwnerIndexToEditModal(null);

		setShowAddOwnerForm(false);
		resetCurrentOwner();
		setOwnerErrors({});
		setContactErrors({});
	};

	// Function to update the contact's value or isPrimary status
	const handleContactChange = (id, field, newValue) => {
		// Filter phone numbers to only allow digits
		let processedValue = newValue;
		if (field === 'value') {
			const contact = currentOwner.contacts.find(c => c.id === id);
			if (contact && contact.type.toUpperCase() === 'PHONE') {
				processedValue = filterContactPhone(newValue);
			}
		}

		setCurrentOwner(prev => ({
			...prev,
			contacts: prev.contacts.map(contact =>
				contact.id === id ? { ...contact, [field]: processedValue } : contact
			),
		}));

		// Validate the contact if it's a value field
		if (field === 'value') {
			const contact = currentOwner.contacts.find(c => c.id === id);
			if (contact) {
				let validationResult = null;
				
				if (contact.type.toUpperCase() === 'EMAIL') {
					validationResult = validateContactEmail(processedValue, 'Email');
				} else if (contact.type.toUpperCase() === 'PHONE') {
					validationResult = validateContactPhone(processedValue, 'Phone');
				}

				// Update contact errors
				if (validationResult && !validationResult.isValid) {
					setContactErrors(prev => ({
						...prev,
						[id]: validationResult.message
					}));
				} else {
					setContactErrors(prev => {
						const newErrors = { ...prev };
						delete newErrors[id];
						return newErrors;
					});
				}
			}
		}
	};

	// Function to remove a contact by id
	const handleRemoveContact = (id) => {
		setCurrentOwner(prev => ({
			...prev,
			contacts: prev.contacts.filter(contact => contact.id !== id),
		}));
		
		// Clear any errors for the removed contact
		setContactErrors(prev => {
			const newErrors = { ...prev };
			delete newErrors[id];
			return newErrors;
		});
	};

	return (
		<Form onSubmit={(e) => e.preventDefault()}>
			{!isUpdateModal && mode !== 'MERCHANT' && (
				<Row>
					<Col md="12">
						<Alert color="info" className="mb-3">
							<i className="ni ni-single-02 mr-2" />
							<strong>Add Owner</strong> - Fill out the form below to add a new owner to the application.
						</Alert>
					</Col>
				</Row>
			)}

			{/* Display Added Owners - Now at the top for both wizard and UpdateMerchantModal */}
			{owners && owners.length > 0 && (
				<>
					<div>
						<h2>
							Merchant Owners ({owners.length})
						</h2>

						<ul className="list-group my-2">
							{owners.map((owner, index) => (
								<li key={index} className={`list-group-item d-flex justify-content-between align-items-center ${(index === ownerIndexToEditModal) ? 'border-primary border-top' : ''}`}>
										<div className="flex-grow-1">
											<h4 className='text-primary'>{owner.firstName} {owner.middleName} {owner.lastName}</h4>
											<div className='d-flex flex-wrap m-0 p-0 pr-2'>
												<span>
													<b>SSN:</b> ***-**-{owner.socialSecurityNumber.slice(-4)} <span className='mx-2'>|</span> 
												</span>
												<span>
													<b>Citizenship:</b> {owner.citizenship} <span className='mx-2'>|</span> 
												</span>
												<span>
													<b>Contacts:</b> {owner.contacts.length} <span className='mx-2'>|</span> 
												</span>
												<span>
													<b>Addresses:</b> {owner.addresses.length} <span className='mx-2'>|</span>
												</span>
												<span>
													<b>Ownership:</b> {owner.ownershipPercentage}%
												</span>
											</div>
										</div>

										<div className="btn-group">
											<Button 
												color="primary"
												size='md'
												onClick={() => handleEditOwner(index)}
												disabled={index === ownerIndexToEditModal}>
												<i className='ni ni-settings-gear-65'></i>
											</Button>
											<Button 
												color="secondary" 
												size='md'
												onClick={() => handleDeleteOwner(index)}
												disabled={locked || index === ownerIndexToEditModal}>
													<i className='ni ni-fat-remove'></i>
											</Button>
										</div>
								</li>
							))}
						</ul>
					</div>
					<hr></hr>
				</>
			)}

			<Collapse isOpen={showAddOwnerForm}>
				{owners.length > 0 && (
					<h2>{isEditMode ? 'Edit Owner' : 'Add another owner'}</h2>
				)}

				{/* Personal Information */}
				<Row>
					<Col md="12">
						<h3 className="text-primary">
							<i className="ni ni-single-02 mr-2" />
							Personal Information
						</h3>
					</Col>

					<Col lg="3" md="6" sm="12">
						<FormGroup className='p-0 m-0 mb-2'>
							<Label htmlFor="firstName" className="mb-1">
								First Name: <span>*</span>
							</Label>
							<Input 
								id="firstName" 
								name="firstName" 
								type="text" 
								value={currentOwner.firstName} 
								onChange={handle} 
								className={ownerErrors.firstName ? 'is-invalid' : ''} 
								readOnly={locked}
								disabled={locked}
								bsSize="sm"
								placeholder='Enter owner first name'
								maxLength={255}
							/>
							{ownerErrors.firstName && <div className="invalid-feedback d-block">{ownerErrors.firstName}</div>}
						</FormGroup>
					</Col>

					<Col lg="3" md="6" sm="12">
						<FormGroup className='p-0 m-0 mb-2'>
							<Label htmlFor="middleName" className="mb-1">Middle Name:</Label>
							<Input
								id="middleName"
								name="middleName"
								type="text"
								value={currentOwner.middleName}
								onChange={handle}
								bsSize="sm"
								placeholder='Enter owner middle name'
								maxLength={255}
							/>
						</FormGroup>
					</Col>

					<Col lg="3" md="6" sm="12">
						<FormGroup className='p-0 m-0 mb-2'>
							<Label htmlFor="lastName" className="mb-1">
								Last Name: <span>*</span>
								</Label>
							<Input 
								id="lastName" 
								name="lastName" 
								type="text" 
								value={currentOwner.lastName} 
								onChange={handle} 
								className={ownerErrors.lastName ? 'is-invalid' : ''} 
								placeholder='Enter owner last name'
								bsSize="sm"
								maxLength={255}
							/>
							{ownerErrors.lastName && <div className="invalid-feedback d-block">{ownerErrors.lastName}</div>}
						</FormGroup>
					</Col>

					<Col lg="3" md="6" sm="12">
						<FormGroup className='p-0 m-0 mb-2'>
							<Label htmlFor="gender" className="mb-1">Gender:</Label>
							<Input id="gender" name="gender" type="select" value={currentOwner.gender} onChange={handle} bsSize="sm">
								<option value="MALE">Male</option>
								<option value="FEMALE">Female</option>
								<option value="OTHER">Other</option>
							</Input>
						</FormGroup>
					</Col>

					<Col lg="3" md="6" sm="12">
						<FormGroup className='p-0 m-0 mb-2'>
							<Label htmlFor="socialSecurityNumber" className="mb-1">
								SSN: <span>*</span>
								</Label>
							<Input 
								id="socialSecurityNumber" 
								name="socialSecurityNumber" 
								type="text" 
								value={currentOwner.socialSecurityNumber} 
								onChange={handle} 
								className={ownerErrors.socialSecurityNumber ? 'is-invalid' : ''} 
								bsSize="sm"
								placeholder="Enter Social Security Number"
							/>
							{ownerErrors.socialSecurityNumber && <div className="invalid-feedback d-block">{ownerErrors.socialSecurityNumber}</div>}
							<small className="text-muted">Format: 123-45-6789</small>
						</FormGroup>
					</Col>

					<Col lg="3" md="6" sm="12">
						<FormGroup className='p-0 m-0 mb-2'>
							<Label htmlFor="birthDate" className="mb-1">Date of Birth:</Label>
							<Input 
								id="birthDate" 
								name="birthDate" 
								type="date" 
								value={currentOwner.birthDate} 
								onChange={handle}
								max={getMaxBirthDate()}
								className={ownerErrors.birthDate ? 'is-invalid' : ''}
								bsSize="sm"
							/>
							{ownerErrors.birthDate && <div className="invalid-feedback d-block">{ownerErrors.birthDate}</div>}
						</FormGroup>
					</Col>

					<Col lg="3" md="6" sm="12">
						<FormGroup className='p-0 m-0 mb-2'>
							<Label htmlFor="citizenship" className="mb-1">
								Citizenship: <span>*</span>
								</Label>
							<Input 
								id="citizenship" 
								name="citizenship" 
								type="text" 
								value={currentOwner.citizenship} 
								onChange={handle} 
								className={ownerErrors.citizenship ? 'is-invalid' : ''} 
								bsSize="sm"
								placeholder='Enter owner citizenship'
							/>
							{ownerErrors.citizenship && <div className="invalid-feedback d-block">{ownerErrors.citizenship}</div>}
						</FormGroup>
					</Col>

					<Col lg="3" md="6" sm="12">
						<FormGroup className='p-0 m-0 mb-2'>
							<Label htmlFor="ownershipPercentage" className="mb-1">
								Ownership %: <span>*</span>
								</Label>
							<Input
								id="ownershipPercentage" 
								name="ownershipPercentage" 
								type="text" 
								value={currentOwner.ownershipPercentage} 
								onChange={handle} 
								placeholder="Enter ownership percentage (1-100)"
								className={ownerErrors.ownershipPercentage ? 'is-invalid' : ''} 
								bsSize="sm"
							/>
							{ownerErrors.ownershipPercentage && <div className="invalid-feedback d-block">{ownerErrors.ownershipPercentage}</div>}
							<small className="text-muted">Enter a value between 1 and 100</small>								
						</FormGroup>
					</Col>
				</Row>

				<hr className='m-0 my-4'></hr>

				{/* Communication Methods */}
				<Row>
					<Col md="12">
						<div className="d-flex justify-content-between align-items-center">
							<h3 className="text-primary">
								<i className="ni ni-mobile-button mr-2"></i>
								Communication Methods
							</h3>
						</div>
					</Col>

					<Col lg="12" xl="6"> 
						<Label>
							Email Addresses <span>*</span>
						</Label>

						<div className='mb-3 px-3'>
							{ currentOwner.contacts
								.filter(contact => contact.type.toUpperCase() === 'EMAIL')
								.map((contact) => (

								<Row className='mb-2 d-flex align-items-center' key={contact.id}>
									<Col xs="12" sm="7" className='m-0 p-0'>
										<FormGroup className='m-0 p-0'>
											<Input
												type='email'
												placeholder={'Enter email address'}
												onChange={(e) => handleContactChange(contact.id, 'value', e.target.value)}
												value={contact.value}
												maxLength={255}
												bsSize="sm"
												invalid={!!contactErrors[contact.id]}
											/>
											{contactErrors[contact.id] && (
												<div className="text-danger small mt-1">{contactErrors[contact.id]}</div>
											)}
										</FormGroup>
									</Col>

									<Col
										xs="10"
										sm={ currentOwner.contacts.filter(c => c.type.toUpperCase() === 'EMAIL').length > 1 ? 4 : 5 } // if more than 1 email, then 4 rows, else 6
										className="m-0 p-0 mt-1 mt-sm-0">
										<FormGroup check inline className="m-0 px-2 d-flex justify-content-start justify-content-sm-end">
											<Label check className="small font-weight-bold ml-2 mr-3">
												Primary?
											</Label>
											<Label check className="mr-3">
												<Input
													type="radio" 
													name={`is-primary-email-${contact.id}`}
													checked={contact.isPrimary} 
													onChange={() => handleContactChange(contact.id, 'isPrimary', true)}
												/> Yes
											</Label>
											<Label check>
												<Input 
													type="radio" 
													name={`is-primary-email-${contact.id}`} 
													checked={!contact.isPrimary} 
													onChange={() => handleContactChange(contact.id, 'isPrimary', false)}
												/> No
											</Label>
										</FormGroup>
									</Col>

									{ currentOwner.contacts.filter(c => c.type.toUpperCase() === 'EMAIL').length > 1 && (
									<Col xs="2" sm="1" className='p-0 mt-2 mt-sm-0 d-flex justify-content-end'>
										<Button
												color="danger" 
												size="sm"
												onClick={() => handleRemoveContact(contact.id)}>
												<i className="ni ni-fat-remove"></i>
										</Button>
									</Col>
									) }
								</Row>
							)) }
						</div>

						<Button
							className='mb-2'
							color="outline-primary" 
							size="sm"
							onClick={() => {
								const newContact = {
									id: Date.now(),
									type: 'EMAIL',
									value: '',
									isPrimary: currentOwner.contacts.filter(c => c.type.toUpperCase() === 'EMAIL').length === 0, 
								};

								setCurrentOwner(prev => ({
									...prev, 
									contacts: [...(prev.contacts || []), newContact] 
								}));
							}}>
							<i className="ni ni-fat-add mr-1"></i>
							Add email
						</Button>
					</Col>

					<Col lg="12" xl="6">
						<Label>
							Phone Numbers <span>*</span>
						</Label>

						<div className='mb-3 px-3'>
							{ currentOwner.contacts
								.filter(contact => contact.type.toUpperCase() === 'PHONE')
								.map((contact) => (
									<Row className='mb-2 d-flex align-items-center' key={contact.id}>
										<Col xs="12" sm="7" className='m-0 p-0'>
											<FormGroup className='m-0 p-0'>
												<Input
													value={contact.value}
													type='tel'
													placeholder={'Enter phone number'}
													maxLength={14}
													bsSize="sm"
													onChange={(e) => handleContactChange(contact.id, 'value', e.target.value)}
													invalid={!!contactErrors[contact.id]}
												/>
												{contactErrors[contact.id] && (
													<div className="text-danger small mt-1">{contactErrors[contact.id]}</div>
												)}
											</FormGroup>
										</Col>

										<Col
											xs="10"
											sm={ currentOwner.contacts.filter(c => c.type.toUpperCase() === 'PHONE').length > 1 ? 4 : 5 }
											className="m-0 p-0 mt-1 mt-sm-0">
											<FormGroup check inline className="m-0 px-2 d-flex justify-content-start justify-content-sm-end">
												<Label check className="small font-weight-bold ml-2 mr-3">
													Primary?
												</Label>
												<Label check className="mr-3">
													<Input 
														type="radio" 
														name={`is-primary-phone-${contact.id}`} 
														checked={contact.isPrimary} 
														onChange={() => handleContactChange(contact.id, 'isPrimary', true)}
													/> Yes
												</Label>
												<Label check>
													<Input 
														type="radio" 
														name={`is-primary-phone-${contact.id}`} 
														checked={!contact.isPrimary} 
														onChange={() => handleContactChange(contact.id, 'isPrimary', false)}
													/> No
												</Label>
											</FormGroup>
										</Col>

										{ currentOwner.contacts.filter(c => c.type.toUpperCase() === 'PHONE').length > 1 && (
											<Col xs="2" sm="1" className='p-0 mt-2 mt-sm-0 d-flex justify-content-end'>
												<Button
													color="danger" 
													size="sm"
													onClick={() => handleRemoveContact(contact.id)}
												>
													<i className="ni ni-fat-remove"></i>
												</Button>
											</Col>
										) }
									</Row>
							)) }
						</div>
						
						<Button
							color="outline-primary" 
							size="sm"
							onClick={() => {
								const newContact = {
									id: Date.now(),
									type: 'PHONE',
									value: '',
									isPrimary: currentOwner.contacts.filter(c => c.type.toUpperCase() === 'PHONE').length === 0, 
								};

								setCurrentOwner(prev => ({
									...prev, 
									contacts: [...(prev.contacts || []), newContact] 
								}));
							}}>
							<i className="ni ni-fat-add mr-1"></i>
							Add phone number
						</Button>
					</Col>
				</Row>

				<hr className='m-0 my-4'></hr>

				{/* Addresses */}
				<Row className="my-2">
					<Col md="12">
						<h3 className="text-primary">
							<i className="ni ni-pin-3 mr-2"></i>
							Addresses
						</h3>
						
						{currentOwner.addresses && currentOwner.addresses.length > 0 ? (
							<ul className="list-group my-2">
								{currentOwner.addresses.map((address, index) => (
									<li key={index} className="list-group-item d-flex justify-content-between align-items-center">
										<div className="flex-grow-1">
											<h4 className='text-primary'>{address.addressType}</h4>
											<p className='fw-bolder text-dark mb-0'>{address.streetAddress}</p>
											<p className='mb-0'>
												{[address.country, address.city, address.state, address.zipCode]
													.filter(field => field && field.trim() !== '')
													.join(', ')}
											</p>
										</div>

										<div className="btn-group">
											<Button 
												color="primary"
												size='md'
												onClick={() => handleEditAddress(address)}>
												<i className='ni ni-settings-gear-65'></i>
											</Button>
											<Button 
												color="secondary" 
												size='md'
												onClick={() => handleRemoveAddress(address)}>
													<i className='ni ni-fat-remove'></i>
											</Button>
										</div>
									</li>
								))}
							</ul>
						) : (
							<div>
								<p className='m-0'>Press the button below to add an address (at least one is required).</p>
							</div>
						)}
						{ownerErrors.addresses && (
							<div className="text-danger small mt-1">{ownerErrors.addresses}</div>
						)}
					</Col>
				</Row>

				<Button
						color="outline-primary" 
						size="sm" 
						onClick={toggleAddressModal}
						disabled={locked || !isFormBeingFilled()}>
							<i className="ni ni-fat-add mr-1"></i>
							Add address
				</Button>
			</Collapse>

			{/* Add Owner Button */}
			<Row className="justify-content-center mt-2">
				<div className='btn-group'>
					{showAddOwnerForm // if
					? ( // then
						<>
							<Button 
								color="primary" 
								onClick={handleAddOwner} 
								disabled={locked || !isFormBeingFilled()}
							>
								<i className="ni ni-check-bold mr-2" />
								{isEditMode ? 'Update owner' : 'Save owner'}
							</Button>

							{ (owners.length >= 1 && !isEditMode) // if
							? ( // then
								<Button
									color="secondary" 
									onClick={() => {
										if (hasFormData()) {
											setShowDiscardOwnerModal(true);
										} else {
											setShowAddOwnerForm(false);
										}
									}} 
								>
									<i className="ni ni-fat-remove mr-2" />
									Discard
								</Button>
							) : ( // else
								<></>
							)}
						</>
					) : ( // else
						<Button 
							color="primary" 
							onClick={() => setShowAddOwnerForm(true)} 
						>
							<i className="ni ni-fat-add mr-2" />
							Add New Owner
						</Button>
					)}

					{isEditMode && (
						<Button 
							color="secondary" 
							onClick={() => setShowCancelOwnerEditModal(true)}
						>
							<i className="ni ni-fat-remove mr-2" />
							Cancel Edition
						</Button>
					)}
				</div>
			</Row>

			<hr></hr>

			{/* Modals */}

			<AddOwnerAddressModal
				isOpen={showAddressModal}
				toggle={toggleAddressModal}
				onAddressAdded={handleAddressAdded}
				addressToEdit={addressToEdit}
				availableAddressTypes={availableAddressTypes}
			/>

			{/* Cancel Owner Update Modal */}
			<Modal isOpen={showCancelOwnerEditModal} toggle={cancelDiscardOwnerEdit} size="sm" centered>
				<ModalHeader toggle={cancelDiscardOwnerEdit}>
					<i className="ni ni-notification-70 mr-2" />
            		Discard Unsaved Changes?
				</ModalHeader>
				<ModalBody>
					You have unsaved owner information. If you proceed, these changes will be lost. Do you really want to clear this form?
				</ModalBody>
				<ModalFooter>
					<Button color="danger" onClick={confirmDiscardOwner}>
						<i className='ni ni-fat-remove mr-2'></i>
						Yes, Discard
					</Button>
					<Button color="secondary" onClick={cancelDiscardOwnerEdit}>
						No
					</Button>
				</ModalFooter>
			</Modal>

			{/* Discard Owner Modal */}
			<Modal isOpen={showDiscardOwnerModal} toggle={cancelDiscardOwner} size="sm" centered>
				<ModalHeader toggle={cancelDiscardOwner}>
					<i className="ni ni-notification-70 mr-2" />
            		Discard Unsaved Changes?
				</ModalHeader>
				<ModalBody>
					You have unsaved owner information. If you proceed, these changes will be lost. Do you really want to clear this form?
				</ModalBody>
				<ModalFooter>
					<Button color="danger" onClick={confirmDiscardOwner}>
						<i className='ni ni-fat-remove mr-2'></i>
						Yes, Discard
					</Button>
					<Button color="secondary" onClick={cancelDiscardOwner}>
						No
					</Button>
				</ModalFooter>
			</Modal>

			{/* Delete Owner Modal */}
			<Modal isOpen={showDeleteOwnerModal} toggle={cancelDeleteOwner} size="sm" centered>
				<ModalHeader toggle={cancelDeleteOwner}>
					<i className="ni ni-fat-remove mr-2" />
					Confirm Owner Removal
				</ModalHeader>
				<ModalBody>
					Are you sure you want to remove this owner?
				</ModalBody>
				<ModalFooter>
					<Button color="danger" onClick={confirmDeleteOwner}>Remove Onwer</Button>
					<Button color="secondary" onClick={cancelDeleteOwner}>Cancel</Button>
				</ModalFooter>
			</Modal>

			{/* Delete Address Modal */}
			<Modal isOpen={showDeleteAddressModal} toggle={cancelDeleteAddress} size="sm" centered>
				<ModalHeader toggle={cancelDeleteAddress}>
					<i className="ni ni-fat-remove mr-2 text-danger" />
					Confirm Address Deletion
				</ModalHeader>
				<ModalBody>
					<p className="mb-0">
						Are you sure you want to delete this address?
					</p>
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={cancelDeleteAddress}>
						<i className="ni ni-fat-remove mr-2" />
						Cancel
					</Button>
					<Button color="danger" onClick={confirmDeleteAddress}>
						<i className="ni ni-fat-remove mr-2" />
						Delete Address
					</Button>
				</ModalFooter>
			</Modal>

			{/* Delete Contact Modal */}
			<Modal isOpen={showDeleteContactModal} toggle={cancelDeleteContact} size="sm">
				<ModalHeader toggle={cancelDeleteContact}>
					<h5 className="mb-0">
						<i className="ni ni-fat-remove mr-2 text-danger" />
						Confirm Contact Deletion
					</h5>
				</ModalHeader>
				<ModalBody>
					<p className="mb-0">
						Are you sure you want to delete the contact "{contactToDelete?.type}"? This action cannot be undone.
					</p>
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={cancelDeleteContact}>
						<i className="ni ni-fat-remove mr-2" />
						Cancel
					</Button>
					<Button color="danger" onClick={confirmDeleteContact}>
						<i className="ni ni-fat-remove mr-2" />
						Delete Contact
					</Button>
				</ModalFooter>
			</Modal>

			{/* Validation feedback modal for Add Owner */}
			<ConfirmationModal
				isOpen={feedbackModalOpen}
				toggle={closeFeedback}
				title="Validation Error"
				message={feedbackMessage}
				confirmText="OK"
				confirmColor="danger"
				icon="ni ni-fat-remove"
			/>

			{/* Edit Confirmation Modal */}
			<Modal isOpen={showEditConfirmationModal} toggle={() => setShowEditConfirmationModal(false)} size="md" centered>
				<ModalHeader toggle={() => setShowEditConfirmationModal(false)}>
						<i className="ni ni-notification-70 mr-2" />
						Confirm Edit Owner
				</ModalHeader>
				<ModalBody>
					<p className="mb-3">
						You have unsaved changes in the form. If you continue to edit the selected owner, 
						your current form data will be lost.
					</p>
					<p className="mb-0">
						<strong>Owner to edit:</strong> {ownerToEdit?.firstName} {ownerToEdit?.lastName}
					</p>
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={() => setShowEditConfirmationModal(false)}>
						<i className="ni ni-fat-remove mr-2" />
						Cancel
					</Button>
					<Button color="primary" onClick={confirmEditOwner}>
						<i className="ni ni-settings-gear-65 mr-2" />
						Continue Edit
					</Button>
				</ModalFooter>
			</Modal>

			{/* Success modal for Owner Saved */}
			<Modal isOpen={showSuccessModal} toggle={() => setShowSuccessModal(false)} size="sm" centered>
				<ModalHeader toggle={() => setShowSuccessModal(false)}>
					<i className="ni ni-check-bold mr-2" />
					Owner {lastAction === 'updated' ? 'Updated' : 'Saved'}
				</ModalHeader>
				<ModalBody>
					<p className="mb-0">
						{lastAction === 'updated' ? (
							<>Owner has been successfully updated.</>
						) : (
							<>The owner <strong>{currentOwner.firstName} {currentOwner.lastName}</strong> has been successfully added to the list. You can add another one or continue to the next step.</>
						)}
					</p>
				</ModalBody>
				<ModalFooter>
					<Button color="primary" onClick={() => {
						setShowSuccessModal(false);
						setShowAddOwnerForm(false);
					}}>
						<i className="ni ni-check-bold mr-2" />
						OK
					</Button>
				</ModalFooter>
			</Modal>
		</Form>
	);
}

export default OwnerInfoStep;
